import { useMemo } from "react";
import type { GraphQLFormattedError } from "graphql";

// Impersonation state read straight from the ?impersonate= URL param, with no
// app imports, so the Apollo client and the write guard can use it.

export interface ImpersonatedUser {
  id: number;
  name: string;
  userType: string;
}

export const IMPERSONATE_URL_PARAM = "impersonate";

/** Parse a ?impersonate= value ("12-Maria Lopez"). */
export function parseImpersonateParam(value: string): ImpersonatedUser | null {
  const dashIndex = value.indexOf("-");
  if (dashIndex === -1) return null;
  const id = parseInt(value.substring(0, dashIndex), 10);
  const name = value.substring(dashIndex + 1);
  if (isNaN(id) || !name) return null;
  return { id, name, userType: "" };
}

/** Parse the impersonated user from the current URL. */
export function getImpersonatedUserFromUrl(): ImpersonatedUser | null {
  if (typeof window === "undefined") return null;
  return parseImpersonateParam(
    new URLSearchParams(window.location.search).get(IMPERSONATE_URL_PARAM) ??
      "",
  );
}

/** Parse the impersonate user ID from the current URL. Used by Apollo link. */
export function getImpersonateUserIdFromUrl(): number | null {
  return getImpersonatedUserFromUrl()?.id ?? null;
}

// The one request that may leave as the viewer while still viewing: the
// "ended_view_as" event, sent before the view is cleared so nothing else ever
// goes out as the viewer from the person's page. It is a TrackEvent carrying
// this flag in its operation context (and in its fetch options, for the fetch
// guard); the header link and both write guards skip it only when the flag and
// the operation name match together, so any other operation with the flag is
// still impersonated and guarded.
export const VIEW_AS_END_EVENT_FLAG = "viewAsEndEvent";
export const VIEW_AS_END_EVENT_OPERATION = "TrackEvent";

/** Whether a request is the flagged end-of-view TrackEvent. */
export function isViewAsEndEvent(
  flag: unknown,
  operationName: string | null | undefined,
): boolean {
  return flag === true && operationName === VIEW_AS_END_EVENT_OPERATION;
}

export function getFirstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** The message for a change blocked before it left the browser. */
export function getImpersonationReadOnlyMessage(name: string): string {
  return `You're viewing as ${getFirstName(name)}. Changes are off.`;
}

/** The message for a change the server refused. */
export function getImpersonationServerBlockedMessage(name: string): string {
  return `${getImpersonationReadOnlyMessage(name)} Nothing was saved.`;
}

// While viewing as someone the server refuses every write with the
// "READ_ONLY_MODE" message, and the guard refuses writes before they leave
// the browser with the same code. Permission checks run against the person
// being viewed, so their refusals are read-only refusals too.
const REFUSAL_CODES: ReadonlySet<string> = new Set([
  "READ_ONLY_MODE",
  "MISSING_ACTION_PERMISSIONS",
  "RESOLVER_IS_ADMIN_ONLY",
]);

/**
 * Whether a mutation error is the server refusing a write because the request
 * is impersonating: the "READ_ONLY_MODE" message (or code), a permission
 * refusal, or the raw read-only guard message staff see.
 */
export function isImpersonationRefusal(error: GraphQLFormattedError): boolean {
  const code = error.extensions?.code;
  if (typeof code === "string" && REFUSAL_CODES.has(code)) return true;
  return (
    error.message === "READ_ONLY_MODE" || error.message.startsWith("blocked")
  );
}

/** Whether changes are blocked: every view as someone is read-only. */
export function useIsImpersonationReadOnly(): boolean {
  return useMemo(() => getImpersonatedUserFromUrl() !== null, []);
}

/**
 * Whether changes are blocked right now. Read outside React (the Apollo
 * client, the fetch guard, DOM listeners).
 */
export function isImpersonationReadOnlyNow(): boolean {
  return getImpersonatedUserFromUrl() !== null;
}

/** The hint under a field the viewer can't change while viewing as someone. */
export function getImpersonationFieldHint(name: string): string {
  return `Changes are off while viewing as ${getFirstName(name)}.`;
}

/**
 * A field's save error while viewing as someone with changes off: any refusal
 * is the read-only block, so it reads as that, never as a generic failure.
 */
export function useImpersonationSaveError(
  saveError: string | undefined,
): string | undefined {
  const user = useMemo(() => getImpersonatedUserFromUrl(), []);
  const readOnly = useIsImpersonationReadOnly();
  if (!saveError || !user || !readOnly) return saveError;
  return getImpersonationFieldHint(user.name);
}
